import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { DataTable, type TableColumnDef } from "../../components/data-table";
import { Button } from "../../components/ui/button";
import { Eye } from "lucide-react";
import { VendorDetailsModal } from "../../components/vendors/VendorDetailsModal";
import { VendorListFilters } from "../../components/vendors/VendorListFilters";
import {
  initialVendorFilters,
  type VendorListFilterValues,
} from "../../components/vendors/vendorFilterUtils";
import { formatVendorArray } from "../../components/vendors/vendorUtils";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  vendorsAPI,
  type Vendor,
  type PaginationParams,
} from "../../services/api";
import { CommonPagination } from "../../components/CommonPagination";
import { useAuth } from "../../contexts/AuthContext";

export default function VendorList() {
  const { can } = useAuth();
  const canRead = can("vendors", "read");

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingVendor, setViewingVendor] = useState<Vendor | null>(null);
  const [loadingVendor, setLoadingVendor] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [filters, setFilters] =
    useState<VendorListFilterValues>(initialVendorFilters);
  const [appliedQuery, setAppliedQuery] = useState<Record<string, string>>({});

  const loadVendors = useCallback(
    async (params?: PaginationParams) => {
      if (!canRead) {
        return;
      }
      setLoading(true);
      try {
        const fetchParams: PaginationParams = {
          page: params?.page ?? currentPage,
          page_size: params?.page_size ?? pageSize,
          ...appliedQuery,
          ...params,
        };

        const res = await vendorsAPI.list(fetchParams);
        if (res && typeof res === "object") {
          const list = res.results ?? res.data ?? [];
          setVendors(Array.isArray(list) ? list : []);
          const count =
            res.count ??
            (res as { total_count?: number }).total_count ??
            (Array.isArray(list) ? list.length : 0);
          setTotalItems(count);
          setTotalPages(Math.ceil(count / (fetchParams.page_size ?? pageSize)));
        }
      } catch {
        toast.error("Failed to load vendors");
        setVendors([]);
      } finally {
        setLoading(false);
      }
    },
    [canRead, currentPage, pageSize, appliedQuery]
  );

  useEffect(() => {
    if (canRead) {
      loadVendors();
    } else {
      setLoading(false);
    }
  }, [canRead, loadVendors]);

  const handleApplyFilters = (queryFilters: Record<string, string>) => {
    setAppliedQuery(queryFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters(initialVendorFilters);
    setAppliedQuery({});
    setCurrentPage(1);
  };

  const openViewModal = async (v: Vendor) => {
    setLoadingVendor(true);
    setIsViewDialogOpen(true);
    setViewingVendor(null);
    try {
      const data = await vendorsAPI.get(v.id);
      setViewingVendor(data);
    } catch {
      toast.error("Failed to load vendor details");
      setIsViewDialogOpen(false);
    } finally {
      setLoadingVendor(false);
    }
  };

  const vendorsTableColumns = useMemo<TableColumnDef<Vendor>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "vendor",
        label: "Vendor Name",
        width: 260,
        renderCell: v => (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded border bg-muted">
              {v.logo || v.logo_url ? (
                <img
                  src={v.logo ?? v.logo_url ?? ""}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                  Logo
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <span className="font-medium">{v.vendor_name}</span>
              <span className="text-xs text-muted-foreground">
                {v.company_name}
              </span>
            </div>
          </div>
        ),
      },
      {
        id: "category",
        label: "Category",
        width: 160,
        renderCell: v => formatVendorArray(v.vendor_category_names),
      },
      {
        id: "location",
        label: "Location",
        width: 140,
        renderCell: v => (
          <span className="text-sm">
            {[v.city, v.state].filter(Boolean).join(", ") || "—"}
          </span>
        ),
      },
      {
        id: "contact",
        label: "Contact",
        width: 160,
        renderCell: v => (
          <div className="flex flex-col text-sm">
            <span>{v.contact_person_name}</span>
            <span className="text-muted-foreground">{v.mobile_number}</span>
          </div>
        ),
      },
      {
        id: "status",
        label: "Verification",
        width: 120,
        renderCell: v => (
          <Badge variant="outline" className="capitalize">
            {v.verification_status ?? "Pending"}
          </Badge>
        ),
      },
      {
        id: "actions",
        label: "Action",
        width: 80,
        alwaysVisible: true,
        headerClassName: "text-right",
        cellClassName: "text-right",
        renderCell: v => (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => openViewModal(v)}
            title="View details"
          >
            <Eye className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  if (!canRead) {
    return (
      <div className="space-y-6 p-6">
        <h1 className="text-3xl font-bold text-foreground">Vendor List</h1>
        <p className="text-muted-foreground">
          You do not have permission to view vendors.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Vendor List</h1>
          <p className="text-muted-foreground">
            Browse empanelled vendors (view only)
          </p>
        </div>
        <Badge variant="secondary" className="shrink-0">
          View only
        </Badge>
      </div>

      <VendorDetailsModal
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        vendor={viewingVendor}
        loading={loadingVendor}
      />

      <VendorListFilters
        filters={filters}
        onFiltersChange={setFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        loading={loading}
      />

      <Card>
        <CardContent className="p-3 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <CustomLoader size="md" />
            </div>
          ) : (
            <DataTable
              title={`All Vendors (${totalItems})`}
              tableId="institution-admin-vendors"
              columns={vendorsTableColumns}
              data={vendors}
              getRowKey={v => v.id}
              emptyMessage="No vendors found. Try adjusting your filters."
            />
          )}

          {totalItems > 0 && (
            <div className="mt-4 border-t pt-4">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={page => {
                  setCurrentPage(page);
                  loadVendors({ page });
                }}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                  loadVendors({ page: 1, page_size: size });
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
